/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomRadioButtonV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React from "react";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Box, Typography } from "@mui/material";
import { componentStyle } from "../../../../config/appTheme";

const size = "default";

const unCheckedIconStyle = ({ disabled = false }) => (
  <Box
    sx={{
      width: size === "small" ? 12 : 15,
      height: size === "small" ? 12 : 15,
      borderRadius: "50%",
      border: disabled ? `2px solid #6f7174` : `2px solid black`,
      transition: "0.25s ease",
    }}
  />
);
const checkedIconStyle = ({ disabled = false }) => (
  <Box
    className="checked-box"
    sx={{
      width: size === "small" ? 12 : 15,
      height: size === "small" ? 12 : 15,
      borderRadius: "50%",
      backgroundColor: disabled ? "#6f7174" : "black",
      border: `2px solid black`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "0.25s ease",
    }}
  >
    <Box
      sx={{
        width: size === "small" ? 4 : 6,
        height: size === "small" ? 4 : 6,
        borderRadius: "50%",
        backgroundColor: "white",
      }}
    />
  </Box>
);

function CustomRadioButtonV2({
  error,
  value,
  onChange,
  data,
  label,
  required,
  description,
  displayDirection = "column",
  name = "radioName",
  spacing = 3, // MUI spacing unit (3 => 24px)
  size = "default",
  page,
  radioColor = null,
  disabled = false,
}) {
  const borderColor = error ? "red" : page === "dynamicform" ? "#fff" : "#000";

  const activeColor = error ? "red" : radioColor ? radioColor : "#26890D";

  const isSmall = size === "small";

  const outerSize = isSmall ? 12 : 15;
  const innerSize = isSmall ? 4 : 6;

  const border = `2px solid ${disabled ? "#6f7174" : borderColor}`;
  const activeBg = disabled ? "#6f7174" : activeColor;

  const hoverColor = disabled
    ? "#6f7174"
    : error
      ? "#992A22"
      : radioColor || "#1F6D0B";

  const UncheckedIcon = (
    <Box
      sx={{
        width: outerSize,
        height: outerSize,
        borderRadius: "50%",
        border,
        transition: "0.25s ease",
      }}
    />
  );

  const CheckedIcon = (
    <Box
      className="checked-box"
      sx={{
        width: outerSize,
        height: outerSize,
        borderRadius: "50%",
        backgroundColor: activeBg,
        border: `2px solid ${activeBg}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "0.25s ease",
      }}
    >
      <Box
        sx={{
          width: innerSize,
          height: innerSize,
          borderRadius: "50%",
          backgroundColor: "white",
        }}
      />
    </Box>
  );

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            mb: 0.5,
            color: "#6f7174",
            ...componentStyle.font.form.textFieldLabel,
            fontWeight: 500,
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </Typography>
      )}

      {/* Description */}
      {description && (
        <Typography
          sx={{
            mb: 0.5,
            ...componentStyle.font.form.textFieldDescription,
            color: "#6f7174",
          }}
        >
          {description}
        </Typography>
      )}

      {/* Radio Group */}
      <RadioGroup
        row
        value={value}
        onChange={onChange}
        sx={{
          display: "flex",
          flexDirection: displayDirection,
          columnGap: spacing,
          pt: 0.5,
          pl: 0.25,
        }}
      >

        {data?.map((item, index) => (
          <FormControlLabel
            key={index}
            value={item.value}
            label={item.label}
            name={name}
            disabled={disabled}
            sx={{
              gap: 1,
              ml: 0,
              mr: spacing,
              cursor: "pointer",
              "& .MuiFormControlLabel-label": {
                ...componentStyle.font.text,
                fontSize: "14px",
              },
            }}
            control={
              <Radio
                disableRipple
                disabled={disabled}
                icon={UncheckedIcon}
                checkedIcon={CheckedIcon}
                sx={{
                  p: 0,
                  "&:hover": {
                    backgroundColor: "#e6e6e6",
                  },
                  "&:hover .checked-box": {
                    backgroundColor: hoverColor,
                    borderColor: hoverColor,
                  },
                }}
              />
            }
          />
        ))}
      </RadioGroup>
    </Box>
  );
}

export default CustomRadioButtonV2;
export { unCheckedIconStyle, checkedIconStyle };
